import { addMonths, isValid } from 'date-fns';
import { type DateInput, toDate, toDateDayjs } from './parse';
import { INVALID_DATE_TEXT } from './format';

/*
 * date-fns' formatDistanceToNow uses different thresholds and wording, so the
 * two helpers below reproduce the English output of moment's `fromNow()` and
 * dayjs' relativeTime plugin exactly ("a few seconds ago", "a minute ago",
 * "2 hours ago", "a day ago", "in 3 days", ...).
 */

const EN = {
  s: 'a few seconds',
  ss: '%d seconds',
  m: 'a minute',
  mm: '%d minutes',
  h: 'an hour',
  hh: '%d hours',
  d: 'a day',
  dd: '%d days',
  M: 'a month',
  MM: '%d months',
  y: 'a year',
  yy: '%d years',
} as const;

type RelativeKey = keyof typeof EN;

const withSuffix = (text: string, isFuture: boolean) =>
  isFuture ? `in ${text}` : `${text} ago`;

const render = (key: RelativeKey, count: number) =>
  EN[key].replace('%d', `${count}`);

const daysToMonths = (days: number) => (days * 4800) / 146097;
const monthsToDays = (months: number) => (months * 146097) / 4800;

/** Whole months from `base` to `other` (base <= other), as moment counts them. */
function positiveMonthsDifference(base: Date, other: Date) {
  let months =
    other.getMonth() -
    base.getMonth() +
    (other.getFullYear() - base.getFullYear()) * 12;
  if (addMonths(base, months).getTime() > other.getTime()) months -= 1;
  const milliseconds = other.getTime() - addMonths(base, months).getTime();
  return { months, milliseconds };
}

/**
 * moment's `moment(value).fromNow()` (en locale, default thresholds).
 * Returns `'Invalid date'` for unparseable input, as moment did.
 */
export function fromNow(value: DateInput, now: Date = new Date()): string {
  const target = toDate(value);
  if (!isValid(target) || !isValid(now)) return INVALID_DATE_TEXT;

  // Duration "from now to target", split into calendar months + remainder.
  const isFuture = now.getTime() < target.getTime();
  const { months, milliseconds } = isFuture
    ? positiveMonthsDifference(now, target)
    : positiveMonthsDifference(target, now);

  const ms = Math.abs(milliseconds);
  const absMonths = Math.abs(months);
  const wholeDays = Math.round(monthsToDays(absMonths));
  const seconds = Math.round(wholeDays * 86400 + ms / 1000);
  const minutes = Math.round(wholeDays * 1440 + ms / 6e4);
  const hours = Math.round(wholeDays * 24 + ms / 36e5);
  const days = Math.round(wholeDays + ms / 864e5);
  const monthCount = Math.round(absMonths + daysToMonths(ms / 864e5));
  const years = Math.round((absMonths + daysToMonths(ms / 864e5)) / 12);

  let text: string;
  if (seconds <= 44) text = render('s', seconds);
  else if (seconds < 45) text = render('ss', seconds);
  else if (minutes <= 1) text = render('m', 1);
  else if (minutes < 45) text = render('mm', minutes);
  else if (hours <= 1) text = render('h', 1);
  else if (hours < 22) text = render('hh', hours);
  else if (days <= 1) text = render('d', 1);
  else if (days < 26) text = render('dd', days);
  else if (monthCount <= 1) text = render('M', 1);
  else if (monthCount < 11) text = render('MM', monthCount);
  else if (years <= 1) text = render('y', 1);
  else text = render('yy', years);

  return withSuffix(text, isFuture);
}

/** dayjs' month difference (`a.diff(b, 'month', true)`). */
function dayjsMonthDiff(a: Date, b: Date): number {
  if (a.getDate() < b.getDate()) return -dayjsMonthDiff(b, a);
  const wholeMonthDiff =
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  const anchor = addMonths(a, wholeMonthDiff).getTime();
  const c = b.getTime() - anchor < 0;
  const anchor2 = addMonths(a, wholeMonthDiff + (c ? -1 : 1)).getTime();
  return +(
    -(
      wholeMonthDiff +
      (b.getTime() - anchor) / (c ? anchor - anchor2 : anchor2 - anchor)
    ) || 0
  );
}

const dayjsUtcOffset = (date: Date) =>
  -Math.round(date.getTimezoneOffset() / 15) * 15;

/**
 * dayjs' `dayjs(value).fromNow()` with the relativeTime plugin (en locale,
 * default thresholds).
 */
export function fromNowDayjs(value: DateInput, now: Date = new Date()): string {
  const target = toDateDayjs(value);
  const diffMs = target.getTime() - now.getTime();
  const zoneDelta = (dayjsUtcOffset(now) - dayjsUtcOffset(target)) * 6e4;
  const diffIn = {
    second: () => diffMs / 1000,
    minute: () => diffMs / 6e4,
    hour: () => diffMs / 36e5,
    day: () => (diffMs - zoneDelta) / 864e5,
    month: () => dayjsMonthDiff(target, now),
    year: () => dayjsMonthDiff(target, now) / 12,
  };
  const thresholds: Array<{
    l: RelativeKey;
    r?: number;
    d?: keyof typeof diffIn;
  }> = [
    { l: 's', r: 44, d: 'second' },
    { l: 'm', r: 89 },
    { l: 'mm', r: 44, d: 'minute' },
    { l: 'h', r: 89 },
    { l: 'hh', r: 21, d: 'hour' },
    { l: 'd', r: 35 },
    { l: 'dd', r: 25, d: 'day' },
    { l: 'M', r: 45 },
    { l: 'MM', r: 10, d: 'month' },
    { l: 'y', r: 17 },
    { l: 'yy', d: 'year' },
  ];

  let result = NaN;
  let out = '';
  for (let i = 0; i < thresholds.length; i += 1) {
    let t = thresholds[i];
    if (t.d) result = diffIn[t.d]();
    const abs = Math.round(Math.abs(result));
    if (t.r === undefined || abs <= t.r) {
      if (abs <= 1 && i > 0) t = thresholds[i - 1];
      out = EN[t.l].replace('%d', `${abs}`);
      break;
    }
  }
  return withSuffix(out, result > 0);
}
